import type { ClientExceptionEntity } from '@DigitaleDoerfer/digitale-doerfer-api';
import { SectionList } from '@legendapp/list/section-list';
import { useScrollToTop } from '@react-navigation/native';
import type {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
  QueryObserverResult,
  RefetchOptions
} from '@tanstack/react-query';
import type { FC, ReactElement } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import type { ScrollViewProps, SectionListRenderItemInfo } from 'react-native';
import { Platform, StyleSheet, View } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import Animated from 'react-native-reanimated';

import { FeedStateView } from '@/features/posts/components/feed/FeedStateView';
import { PostScreenFeedCard } from '@/features/posts/components/feed/PostScreenCards';
import { OrganizationPostDateSectionHeader } from '@/features/posts/components/organization-posts/OrganizationPostDateSectionHeader';
import { useTrackFeedPostViews } from '@/features/posts/hooks/useTrackFeedPostViews';
import type { GenericPostDetail } from '@/features/posts/types/posts';
import { groupPostsByDay } from '@/features/posts/utils/groupPostsByDay';
import { Refresher } from '@/shared/components/state-view/Refresher';
import { hasItems } from '@/shared/utils/array';

const makeStyles = () =>
  StyleSheet.create({
    container: { flex: 1, padding: 12 }
  });

type OrganizationPostSection = {
  dayKey: string;
  dayStart: number;
  data: GenericPostDetail[];
};

type OrganizationPostsListProps = {
  posts?: GenericPostDetail[];
  isLoading: boolean;
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<
    InfiniteQueryObserverResult<GenericPostDetail[], ClientExceptionEntity>
  >;
  hasNextPage: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  refetch: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<GenericPostDetail[], ClientExceptionEntity>>;
  isRefetching: boolean;
  hideEndView?: boolean;
  header?: ReactElement;
};

export const OrganizationPostsList: FC<OrganizationPostsListProps> = ({
  posts,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  refetch,
  isRefetching,
  hideEndView,
  header
}) => {
  const styles = useMemo(() => makeStyles(), []);

  const sections = useMemo<OrganizationPostSection[]>(
    () =>
      groupPostsByDay(posts).map((group) => ({
        dayKey: group.dayKey,
        dayStart: group.dayStart,
        data: group.posts
      })),
    [posts]
  );

  const viewabilityConfigCallbackPairs =
    useTrackFeedPostViews<
      SectionListRenderItemInfo<GenericPostDetail>['item']
    >();

  const listRef = useRef(null);
  useScrollToTop(listRef);

  const renderItem = useCallback(
    ({
      item
    }: SectionListRenderItemInfo<
      GenericPostDetail,
      OrganizationPostSection
    >) => <PostScreenFeedCard post={item} />,
    []
  );

  const renderSectionHeader = useCallback(
    ({ section }: { section: OrganizationPostSection }) => (
      <OrganizationPostDateSectionHeader dayStart={section.dayStart} />
    ),
    []
  );

  const keyExtractor = useCallback(
    (item: GenericPostDetail, index: number) => `post-${item.postId ?? index}`,
    []
  );

  const renderScrollComponent = useCallback(
    (scrollProps: ScrollViewProps) => <Animated.ScrollView {...scrollProps} />,
    []
  );

  return (
    <SectionList<GenericPostDetail, OrganizationPostSection>
      ref={listRef}
      style={styles.container}
      sections={sections}
      renderScrollComponent={
        Platform.OS === 'web' ? renderScrollComponent : undefined
      }
      refreshControl={
        <Refresher refreshing={isRefetching} onRefresh={refetch} />
      }
      renderItem={renderItem}
      renderSectionHeader={renderSectionHeader}
      keyExtractor={keyExtractor}
      stickySectionHeadersEnabled={false}
      estimatedItemSize={220}
      recycleItems
      maintainVisibleContentPosition
      drawDistance={1200}
      ListHeaderComponent={header}
      ListEmptyComponent={
        <View>
          {isLoading && <ActivityIndicator size={80} />}
          {!hasItems(posts) && !hasNextPage && !isLoading && (
            <FeedStateView state="empty" />
          )}
        </View>
      }
      ListFooterComponent={
        <View>
          {isFetchingNextPage && <ActivityIndicator size={80} />}
          {!hasNextPage && !isLoading && !hideEndView && hasItems(posts) && (
            <FeedStateView />
          )}
        </View>
      }
      extraData={[
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        hideEndView,
        sections
      ]}
      onEndReached={() => hasNextPage && !isFetchingNextPage && fetchNextPage()}
      onEndReachedThreshold={0.8}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      viewabilityConfigCallbackPairs={viewabilityConfigCallbackPairs}
    />
  );
};
